import { DOCUMENT } from '@angular/common';
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  AfterViewInit,
  Renderer2,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { ConfigService } from 'src/app/config/config.service';
import { RightSidebarService } from 'src/app/core/service/rightsidebar.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UnsubscribeOnDestroyAdapter } from 'src/app/shared/UnsubscribeOnDestroyAdapter';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-right-sidebar',
  templateUrl: './right-sidebar.component.html',
  styleUrls: ['./right-sidebar.component.sass']
})
export class RightSidebarComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit, AfterViewInit
{
  selectedBgColor = 'black';
  maxHeight: string;
  maxWidth: string;
  showpanel = false;
  isOpenSidebar: boolean = false;
  isDarkSidebar = true;
  isDarTheme = true;
  isRtl = false;
  public config: any = {};

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private rightSidebarService: RightSidebarService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.config = this.configService.configData;

    this.subs.sink = this.rightSidebarService.sidebarState.subscribe(
      (isRunning) => {
        this.isOpenSidebar = isRunning;
        this.cdr.markForCheck();
      }
    );

    this.setRightSidebarWindowHeight();

    this.initDefaultTheme();
    this.applySavedTheme();
  }

  ngAfterViewInit(): void {
    this.renderer.addClass(this.document.body, 'submenu-closed');

    if (localStorage.getItem('isRtl')) {
      if (localStorage.getItem('isRtl') === 'true') {
        this.setRTLSettings();
      } else {
        this.setLTRSettings();
      }
    } else {
      if (this.config.layout.rtl === true) {
        this.setRTLSettings();
      } else {
        this.setLTRSettings();
      }
    }

    this.cdr.markForCheck();
  }

  initDefaultTheme(): void {
    if (!localStorage.getItem('theme')) {
      localStorage.setItem('theme', 'dark');
    }

    if (!localStorage.getItem('menuOption')) {
      localStorage.setItem('menuOption', 'menu_dark');
    }

    if (!localStorage.getItem('choose_logoheader')) {
      localStorage.setItem('choose_logoheader', 'logo-black');
    }

    if (!localStorage.getItem('choose_skin')) {
      localStorage.setItem('choose_skin', 'theme-black');
    }

    if (!localStorage.getItem('choose_skin_active')) {
      localStorage.setItem('choose_skin_active', 'black');
    }
  }

  applySavedTheme(): void {
    const theme = localStorage.getItem('theme') || 'dark';
    const menuOption = localStorage.getItem('menuOption') || 'menu_dark';
    const chooseSkin = localStorage.getItem('choose_skin') || 'theme-black';
    const chooseSkinActive = localStorage.getItem('choose_skin_active') || 'black';
    const logoHeader = localStorage.getItem('choose_logoheader') || 'logo-black';

    this.clearThemeClasses();

    if (theme === 'dark') {
      this.renderer.addClass(this.document.body, 'dark');
      this.isDarTheme = true;
    } else {
      this.renderer.addClass(this.document.body, 'light');
      this.isDarTheme = false;
    }

    if (menuOption === 'menu_dark') {
      this.renderer.addClass(this.document.body, 'menu_dark');
      this.isDarkSidebar = true;
    } else {
      this.renderer.addClass(this.document.body, 'menu_light');
      this.isDarkSidebar = false;
    }

    this.renderer.addClass(this.document.body, logoHeader);
    this.renderer.addClass(this.document.body, chooseSkin);
    this.renderer.addClass(this.document.body, 'submenu-closed');

    this.selectedBgColor = chooseSkinActive;

    this.cdr.markForCheck();
  }

  clearThemeClasses(): void {
    const body = this.document.body;

    const classesToRemove = [
      'light',
      'dark',
      'menu_light',
      'menu_dark',
      'logo-white',
      'logo-black',
      'theme-white',
      'theme-black',
      'theme-purple',
      'theme-orange',
      'theme-cyan',
      'theme-green',
      'theme-blue'
    ];

    classesToRemove.forEach((className) => {
      this.renderer.removeClass(body, className);
    });
  }

  selectTheme(e: string): void {
    this.selectedBgColor = e;

    this.clearThemeColorClasses();

    this.renderer.addClass(this.document.body, 'theme-' + this.selectedBgColor);

    localStorage.setItem('choose_skin', 'theme-' + this.selectedBgColor);
    localStorage.setItem('choose_skin_active', this.selectedBgColor);

    this.cdr.markForCheck();
  }

  clearThemeColorClasses(): void {
    const colors = [
      'theme-white',
      'theme-black',
      'theme-purple',
      'theme-orange',
      'theme-cyan',
      'theme-green',
      'theme-blue'
    ];

    colors.forEach((className) => {
      this.renderer.removeClass(this.document.body, className);
    });
  }

  lightSidebarBtnClick(): void {
    this.renderer.removeClass(this.document.body, 'menu_dark');
    this.renderer.removeClass(this.document.body, 'logo-black');

    this.renderer.addClass(this.document.body, 'menu_light');
    this.renderer.addClass(this.document.body, 'logo-white');

    this.isDarkSidebar = false;

    localStorage.setItem('choose_logoheader', 'logo-white');
    localStorage.setItem('menuOption', 'menu_light');

    this.cdr.markForCheck();
  }

  darkSidebarBtnClick(): void {
    this.renderer.removeClass(this.document.body, 'menu_light');
    this.renderer.removeClass(this.document.body, 'logo-white');

    this.renderer.addClass(this.document.body, 'menu_dark');
    this.renderer.addClass(this.document.body, 'logo-black');

    this.isDarkSidebar = true;

    localStorage.setItem('choose_logoheader', 'logo-black');
    localStorage.setItem('menuOption', 'menu_dark');

    this.cdr.markForCheck();
  }

  lightThemeBtnClick(): void {
    this.clearThemeClasses();

    this.renderer.addClass(this.document.body, 'light');
    this.renderer.addClass(this.document.body, 'submenu-closed');
    this.renderer.addClass(this.document.body, 'menu_light');
    this.renderer.addClass(this.document.body, 'logo-white');
    this.renderer.addClass(this.document.body, 'theme-white');

    this.selectedBgColor = 'white';
    this.isDarkSidebar = false;
    this.isDarTheme = false;

    localStorage.setItem('theme', 'light');
    localStorage.setItem('menuOption', 'menu_light');
    localStorage.setItem('choose_logoheader', 'logo-white');
    localStorage.setItem('choose_skin', 'theme-white');
    localStorage.setItem('choose_skin_active', 'white');

    this.cdr.markForCheck();
  }

  darkThemeBtnClick(): void {
    this.clearThemeClasses();

    this.renderer.addClass(this.document.body, 'dark');
    this.renderer.addClass(this.document.body, 'submenu-closed');
    this.renderer.addClass(this.document.body, 'menu_dark');
    this.renderer.addClass(this.document.body, 'logo-black');
    this.renderer.addClass(this.document.body, 'theme-black');

    this.selectedBgColor = 'black';
    this.isDarkSidebar = true;
    this.isDarTheme = true;

    localStorage.setItem('theme', 'dark');
    localStorage.setItem('menuOption', 'menu_dark');
    localStorage.setItem('choose_logoheader', 'logo-black');
    localStorage.setItem('choose_skin', 'theme-black');
    localStorage.setItem('choose_skin_active', 'black');

    this.cdr.markForCheck();
  }

  setRightSidebarWindowHeight(): void {
    const height = window.innerHeight - 60;
    this.maxHeight = height + '';
    this.maxWidth = '500px';
  }

  onClickedOutside(event: Event): void {
    const button = event.target as HTMLButtonElement;

    if (button.id !== 'settingBtn') {
      if (this.isOpenSidebar === true) {
        this.toggleRightSidebar();
      }
    }
  }

  toggleRightSidebar(): void {
    this.isOpenSidebar = !this.isOpenSidebar;
    this.rightSidebarService.setRightSidebar(this.isOpenSidebar);
    this.cdr.markForCheck();
  }

  switchDirection(event: MatSlideToggleChange): void {
    const isrtl: string = String(event.checked);

    if (
      isrtl === 'false' &&
      document.getElementsByTagName('html')[0].hasAttribute('dir')
    ) {
      document.getElementsByTagName('html')[0].removeAttribute('dir');
      this.renderer.removeClass(this.document.body, 'rtl');
    } else if (
      isrtl === 'true' &&
      !document.getElementsByTagName('html')[0].hasAttribute('dir')
    ) {
      document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl');
      this.renderer.addClass(this.document.body, 'rtl');
    }

    localStorage.setItem('isRtl', isrtl);
    this.isRtl = event.checked;

    this.cdr.markForCheck();
  }

  setRTLSettings(): void {
    document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl');
    this.renderer.addClass(this.document.body, 'rtl');
    this.isRtl = true;
    localStorage.setItem('isRtl', 'true');
  }

  setLTRSettings(): void {
    document.getElementsByTagName('html')[0].removeAttribute('dir');
    this.renderer.removeClass(this.document.body, 'rtl');
    this.isRtl = false;
    localStorage.setItem('isRtl', 'false');
  }
}
